import { defineStore } from 'pinia';
import { streamTrademarksOfUser, updateTrademark } from "@/services/trademarks.js";
import { streamDocketingOfUser, getDocketTasksByTrademarkSerialNumberByDeadline, updateDocketTask } from "@/services/trademark_docketing.js";
import { hasForeignTrademarks, streamForeignTrademarksOfUser, updateForeignTrademark } from "@/services/tm_foreign.js";
import { streamForeignDocketingOfUser, getForeignDocketTasksByTrademarkId, updateForeignDocketTask } from "@/services/tm_foreign_docketing.js";
import { getTrademarkLogsBySerialNumber, updateTrademarkLog } from "@/services/logging.js";
import { db } from "@/services/db.js";
import { ref } from 'vue';

// US (USPTO-synced) and foreign (manual/imported) marks live in separate
// collections but are presented as one portfolio. Rows are keyed by doc id —
// for US marks the doc id IS the serial number, so task.serialNumber joins
// work uniformly. Foreign rows/tasks are tagged `foreign: true` at the
// snapper (NOT here — spreading a reactive row into `{ foreign: true, ...tm }`
// would produce non-reactive copies, which silently breaks row checkbox
// selection in the tables), so rendering and updates can route to the right
// collection. Firms without foreign marks skip the foreign streams entirely
// (see loadData), so the merged views are the US lists themselves.
const mergedTrademarks = (state) =>
  state.foreign_trademarks.length === 0
    ? state.us_trademarks
    : [...state.us_trademarks, ...state.foreign_trademarks];
const mergedDockets = (state) =>
  state.foreign_dockets.length === 0
    ? state.us_dockets
    : [...state.us_dockets, ...state.foreign_dockets];
// task.serialNumber is the parent matter's doc id (== serial number for US
// marks); rows always carry `id` from the snapshot
const findMatterForTask = (trademarks, task) =>
  trademarks.find((t) => (t.id || t.serialNumber) === task.serialNumber);

export const useTrademarkStore = defineStore('trademark', {
  state: () => ({
    firmId: null,
    userId: null,
    us_trademarks: [],
    foreign_trademarks: [],
    currentTrademarkId: null,
    us_dockets: [],
    foreign_dockets: [],
    showForeign: false,
    tm_loaded: false,
    foreign_tm_loaded: false,
    docket_loaded: false,
    foreign_docket_loaded: false,
    unsubscribeTm: null,
    unsubscribeDocketing: null,
    unsubscribeForeignTm: null,
    unsubscribeForeignDocketing: null,
    // name lookups pushed by the Trademarks view after it loads members/clients,
    // so sort/filter/export can resolve client ids and assigned uids to names
    clientNames: {},
    memberNames: {},
    trademark_columns: [
      {
        name: "Serial No.",
        sortStatus: "a",
        checked: true,
        fieldName: "serialNumber",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Registration No.",
        sortStatus: "a",
        checked: true,
        fieldName: "registrationNumber",
        filterStr: "",
        filterFlag: false,
      },
      {
        // virtual column: "US" for USPTO marks, the jurisdiction code for
        // foreign marks (resolved in trademarkFieldValue)
        name: "Country",
        sortStatus: "a",
        checked: true,
        fieldName: "country",
        filterStr: "",
        filterFlag: false,
        filterOptions: [],
      },
      {
        // foreign matters only — the local agent; always blank for US marks
        name: "Foreign Associate",
        sortStatus: "a",
        checked: true,
        fieldName: "foreignAssociate",
        filterStr: "",
        filterFlag: false,
      },
      {
        // foreign matters only — the associate's reference no.
        name: "Foreign Associate Ref",
        sortStatus: "a",
        checked: true,
        fieldName: "foreignAssociateRef",
        filterStr: "",
        filterFlag: false,
      },
      {
        // read from statusObj.status.attrnyDktNumber (TSDR) at render time
        name: "Attorney Docket No.",
        sortStatus: "a",
        checked: true,
        fieldName: "attrnyDktNumber",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Mark",
        sortStatus: "a",
        checked: true,
        fieldName: "wordMark",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "IC",
        sortStatus: "a",
        checked: true,
        fieldName: "internationalClass",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Applicant",
        sortStatus: "a",
        checked: true,
        fieldName: "applicantName",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Status",
        sortStatus: "a",
        checked: true,
        fieldName: "alive",
        filterStr: "",
        filterFlag: false,
        filterOptions: [],
      },
      {
        // resolved to the client name via the clientNames lookup
        name: "Client",
        sortStatus: "a",
        checked: true,
        fieldName: "client",
        filterStr: "",
        filterFlag: false,
        filterOptions: [],
      },
      {
        // uid list resolved to names via the memberNames lookup
        name: "Assigned",
        sortStatus: "a",
        checked: true,
        fieldName: "assigned",
        filterStr: "",
        filterFlag: false,
        filterOptions: [],
      },
    ],
    the_docket_columns: [
      {
        name: "Serial No.",
        sortStatus: "a",
        checked: true,
        fieldName: "serialNumber",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Registration No.",
        sortStatus: "a",
        checked: true,
        fieldName: "registrationNumber",
        filterStr: "",
        filterFlag: false,
      },
      {
        // virtual column: resolved from the parent matter like
        // attrnyDktNumber — "US" for USPTO marks, jurisdiction for foreign
        name: "Country",
        sortStatus: "a",
        checked: true,
        fieldName: "country",
        filterStr: "",
        filterFlag: false,
        filterOptions: [],
      },
      {
        // resolved from the parent matter — tasks don't carry these;
        // foreign matters only, always blank for US marks
        name: "Foreign Associate",
        sortStatus: "a",
        checked: true,
        fieldName: "foreignAssociate",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Foreign Associate Ref",
        sortStatus: "a",
        checked: true,
        fieldName: "foreignAssociateRef",
        filterStr: "",
        filterFlag: false,
      },
      {
        // resolved from the trademark's statusObj.status.attrnyDktNumber (TSDR)
        // via the store lookup at render time — tasks don't carry statusObj
        name: "Attorney Docket No.",
        sortStatus: "a",
        checked: true,
        fieldName: "attrnyDktNumber",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Mark",
        sortStatus: "a",
        checked: true,
        fieldName: "wordMark",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Task",
        sortStatus: "a",
        checked: true,
        fieldName: "title",
        filterStr: "",
        filterFlag: false,
      },
      {
        name: "Deadline",
        sortStatus: "a",
        checked: true,
        fieldName: "deadline",
        filterStr: "",
        filterFlag: false,
        // date-range filter (start/end pickers) instead of the free-text box,
        // same design as patent docketing. Defaults span everything so
        // enabling the filter alone never hides a deadline
        dateRange: true,
        filterStart: "1970-01-01",
        filterEnd: "9999-12-31",
      },
      {
        name: "Event Type",
        sortStatus: "a",
        checked: true,
        fieldName: "eventType",
        filterStr: "",
        filterFlag: false,
        filterOptions: [],
      },
      {
        name: "Status",
        sortStatus: "a",
        checked: true,
        fieldName: "status",
        filterStr: "",
        filterFlag: false,
        filterOptions: [],
      },
      {
        name: "Action",
        sortStatus: "",
        checked: true,
        fieldName: "",
        filterStr: "",
        filterFlag: false,
        nosort: true,
      },
    ],
    showCompleted: false,
    showDead: false,
    showArchived: false,
    trademark_sort: null,
    docket_sort: null,
  }),
  getters: {
    filteredSortedTrademarks: (state) => {
      // most columns read the doc's own field; virtual columns resolve from
      // nested data or the name lookups (kept out of the per-row hot path)
      const trademarkFieldValue = (tm, fieldName) => {
        if (fieldName === "attrnyDktNumber") return tm.statusObj?.status?.attrnyDktNumber ?? "";
        if (fieldName === "country") return tm.foreign ? (tm.jurisdiction || "(None)") : "US";
        if (fieldName === "alive") return tm.alive ? "Live" : "Dead";
        if (fieldName === "client") return (tm.client && state.clientNames[tm.client]) || "(None)";
        if (fieldName === "assigned") {
          const assigned = Array.isArray(tm.assigned) ? tm.assigned : [];
          const names = assigned.map((uid) => state.memberNames[uid] || uid);
          return names.length ? names.join("; ") : "(None)";
        }
        return tm[fieldName];
      };
      let filterColumns = state.trademark_columns.filter((c) => { return c.filterFlag == true })
        .map((c) => {
          if (c.filterOptions) {
            c.filterArray = c.filterOptions.reduce((optArray, opt) => {
              if (opt.value) {
                optArray.push(opt.label);
              }
              return optArray;
            }, []);
          }
          return c;
        });

      let filteredTms = []

      // foreign marks join the table when the Show Foreign toggle is on —
      // and always in archived mode, which shows US and foreign together
      const visibleTms = mergedTrademarks(state).filter((x) => state.showArchived || state.showForeign || !x.foreign);

      if (filterColumns.length == 0) {
        filteredTms = visibleTms.filter((x) => {
          if (state.showArchived) {
            return !!x.archived;
          } else if (state.showDead) {
            // "also show dead": alive and dead together, still hiding archived
            return !x.archived;
          } else {
            return x.alive && !x.archived;
          }
        });
      } else {
        filteredTms = visibleTms.filter((x) => {
          if (state.showArchived) {
            if (!x.archived) return false;
          } else if (state.showDead) {
            if (x.archived) return false;
          } else {
            if (!x.alive || x.archived) return false;
          }
          for (const filterColumn of filterColumns) {
            var field_name = filterColumn.fieldName;

            let value = trademarkFieldValue(x, field_name);
            if (Array.isArray(value)) {
              value = value.join(" ");
            }
            if (filterColumn.filterArray) {
              // multi-valued column: a trademark matches when ANY of its
              // resolved assignee names is selected
              if (field_name === "assigned") {
                const names = value ? value.split("; ") : [];
                if (!names.some((n) => filterColumn.filterArray.includes(n))) return false;
              } else if (!filterColumn.filterArray.includes(value)) {
                return false;
              }
            } else if (!value || !value.toUpperCase().includes(filterColumn.filterStr.toUpperCase())) {
              return false;
            }
          }
          return true;
        });
      }
      let localSortColumn = state.trademark_sort;
      if (localSortColumn) {
        return filteredTms.toSorted(function (a, b) {
          let keyA = trademarkFieldValue(a, localSortColumn.fieldName);
          let keyB = trademarkFieldValue(b, localSortColumn.fieldName);
          keyA = Array.isArray(keyA) ? (keyA[0] ?? "") : (keyA ?? "");
          keyB = Array.isArray(keyB) ? (keyB[0] ?? "") : (keyB ?? "");
          if (localSortColumn.sortStatus == "a") {
            return keyA.localeCompare(keyB);
          } else {
            return keyB.localeCompare(keyA);
          }
        });
      } else {
        return filteredTms;
      }
    },
    filteredSortedDockets: (state) => {
      // most columns read the task's own field; virtual columns resolve from
      // the parent trademark (kept out of the per-row hot path above)
      const docketFieldValue = (task, fieldName) => {
        if (fieldName === "serialNumber" && task.foreign) {
          // a foreign task's serialNumber is the parent matter's doc id (the
          // join key); sort/filter by the matter's real application number
          const tm = findMatterForTask(mergedTrademarks(state), task);
          return tm?.serialNumber || "";
        }
        if (fieldName === "attrnyDktNumber") {
          const tm = findMatterForTask(mergedTrademarks(state), task);
          return tm?.statusObj?.status?.attrnyDktNumber ?? "";
        }
        if (fieldName === "country") {
          const tm = findMatterForTask(mergedTrademarks(state), task);
          return tm ? (tm.foreign ? (tm.jurisdiction || "(None)") : "US") : "";
        }
        if (fieldName === "foreignAssociate" || fieldName === "foreignAssociateRef") {
          const tm = findMatterForTask(mergedTrademarks(state), task);
          return tm?.[fieldName] || "";
        }
        return task[fieldName];
      };
      let filterColumns = state.the_docket_columns.filter((c) => { return c.filterFlag == true })
        .map((c) => {
          if (c.filterOptions) {
            c.filterArray = c.filterOptions.reduce((optArray, opt) => {
              if (opt.value) {
                optArray.push(opt.label);
              }
              return optArray;
            }, []);
          }
          return c;
        });

      let filteredDockets = []

      const allDockets = mergedDockets(state);

      if (filterColumns.length == 0) {
        filteredDockets = allDockets.filter((x) => {
          if (x.archived) return false;
          if (state.showCompleted) {
            return x.status == "Completed";
          } else {
            return x.status != "Completed";
          }
        });
      } else {
        filteredDockets = allDockets.filter((x) => {
          if (x.archived) return false;

          if (state.showCompleted != (x.status == "Completed")) {
            return false;
          }
          for (const filterColumn of filterColumns) {
            var field_name = filterColumn.fieldName;

            // date-range columns (deadline): inclusive [start, end], either
            // side optional; tasks with no deadline are excluded while a
            // range filter is active
            if (filterColumn.dateRange) {
              const v = x[field_name];
              if (!v) return false;
              if (filterColumn.filterStart && v < filterColumn.filterStart) return false;
              if (filterColumn.filterEnd && v > filterColumn.filterEnd) return false;
              continue;
            }

            let value = "";
            if (Array.isArray(x[field_name])) {
              value = x[field_name].join(" ");
            }
            else {
              value = docketFieldValue(x, field_name);
            }

            if (filterColumn.filterArray && !filterColumn.filterArray.includes(value)) {
              return false;
            } else {
              if (!value || !value.toUpperCase().includes(filterColumn.filterStr.toUpperCase())) {
                return false;
              }
            }
          }
          return true;
        });
      }
      let localSortColumn = state.docket_sort;
      if (localSortColumn) {
        return filteredDockets.toSorted(function (a, b) {
          var keyA = "";
          var keyB = "";
          if (Array.isArray(a[localSortColumn.fieldName])) {
            keyA = a[localSortColumn.fieldName][0] ? a[localSortColumn.fieldName][0] : "";
            keyB = b[localSortColumn.fieldName][0] ? b[localSortColumn.fieldName][0] : "";
          }
          else {
            keyA = docketFieldValue(a, localSortColumn.fieldName) || "";
            keyB = docketFieldValue(b, localSortColumn.fieldName) || "";
          }
          if (localSortColumn.sortStatus == "a") {
            return keyA.localeCompare(keyB);
          } else {
            return keyB.localeCompare(keyA);
          }
        });
      } else {
        // No user-selected sort: the US and foreign streams are each
        // deadline-ordered, but concatenating them puts every foreign task
        // after the last US task — with pagination that buries them on the
        // final page. Default to deadline order so the merged portfolio
        // interleaves (a no-op ordering for US-only firms, whose stream
        // already arrives deadline-ordered).
        return filteredDockets.toSorted((a, b) =>
          (a.deadline || "").localeCompare(b.deadline || "")
        );
      }
    },
    currentTrademark: (state) => {
      if (!state.currentTrademarkId) {
        return null;
      }
      // foreign matters are addressed by doc id; US marks by serial number
      // (which is also their doc id)
      return mergedTrademarks(state).find(
        (tm) => tm.id === state.currentTrademarkId || tm.serialNumber === state.currentTrademarkId
      ) || null;
    },
    // merged, foreign-tagged views over the per-collection streams
    the_trademarks: (state) => mergedTrademarks(state),
    the_dockets: (state) => mergedDockets(state),
    // "fully loaded" = the US stream delivered AND the foreign question is
    // settled (the probe found nothing, or the first foreign snapshot
    // arrived). Use these wherever concluding from a partially merged list
    // would be wrong — e.g. IndividualTrademark's notFound when deep-linking
    // a foreign matter. Lists/tables that render progressively should keep
    // using tm_loaded/docket_loaded.
    tm_fully_loaded: (state) => state.tm_loaded && state.foreign_tm_loaded,
    docket_fully_loaded: (state) => state.docket_loaded && state.foreign_docket_loaded,
    tm_columns: (state) => state.trademark_columns,
    tm_sort_column: (state) => state.trademark_sort,
    docket_columns: (state) => state.the_docket_columns,
    docket_sort_column: (state) => state.docket_sort,
  },
  actions: {


    loadData(firmId, userId) {
      if (!firmId || !userId) {
        throw new Error('firm and user required');
      }
      const firmIdOrUserChanged = firmId != this.firmId || userId != this.userId;
      // v2: column visibility persisted keyed by fieldName — the v1 positional
      // arrays silently mis-hid columns whenever the column list changed.
      // Missing keys default to the column's shipped `checked`.
      const applyColumnChecks = (key, columns) => {
        const checks = JSON.parse(localStorage.getItem(key) || "null");
        if (checks && typeof checks === "object" && !Array.isArray(checks)) {
          columns.forEach((c) => {
            const k = c.fieldName || c.name;
            if (k in checks) c.checked = checks[k];
          });
        }
      };
      applyColumnChecks("trademark_docketing_columns_checked_v2", this.the_docket_columns);
      applyColumnChecks("trademark_columns_checked_v2", this.trademark_columns);
      if (firmIdOrUserChanged) {
        //get rid of loaded at code. maybe need error handling to reset when the listener fails.
        this.tm_loaded = false;
        this.docket_loaded = false;
        this.foreign_tm_loaded = false;
        this.foreign_docket_loaded = false;
        this.firmId = firmId;
        this.userId = userId;
        if (this.unsubscribeTm && typeof this.unsubscribeTm === 'function') {
          this.unsubscribeTm();
          this.unsubscribeTm = null;
        }
        if (this.unsubscribeDocketing && typeof this.unsubscribeDocketing === 'function') {
          this.unsubscribeDocketing();
        }
        // streamTrademarksOfUser is async (role lookup first); guard against a
        // firm/user switch happening before the subscription resolves
        streamTrademarksOfUser(firmId, userId, this.tradeMarkSnapper).then((unsub) => {
          if (this.firmId !== firmId || this.userId !== userId) {
            unsub();
          } else {
            this.unsubscribeTm = unsub;
          }
        });
        // docketing tasks are readable by assigned clients too (same rules model
        // as patent docketing) — stream for EVERY role; streamDocketingOfUser
        // scopes non-manager/admin roles (member and client alike) by assigned[]
        streamDocketingOfUser(firmId, userId, this.docketSnapper).then((unsub) => {
          if (this.firmId !== firmId || this.userId !== userId) {
            unsub();
          } else {
            this.unsubscribeDocketing = unsub;
          }
        });
        // foreign marks and their manual docketing tasks — separate
        // collections, same role-aware scoping; merged into the shared lists
        // by the the_trademarks/the_dockets getters
        if (this.unsubscribeForeignTm && typeof this.unsubscribeForeignTm === 'function') {
          this.unsubscribeForeignTm();
          this.unsubscribeForeignTm = null;
        }
        if (this.unsubscribeForeignDocketing && typeof this.unsubscribeForeignDocketing === 'function') {
          this.unsubscribeForeignDocketing();
          this.unsubscribeForeignDocketing = null;
        }
        this._foreignStreaming = false;
        this.probeAndAttachForeignStreams(firmId, userId);
        //TODO do unsubscribe/resubscribe on errors. maybe on leaving the page and resubscribe on load?
      }
    },
    // Most firms never touch tm-foreign, so probe once (a single limit(1)
    // read) and attach the live foreign streams only when there is data to
    // stream — firms without foreign marks pay neither the listeners nor the
    // merge. Members/clients probe their assigned[] scope (an unfiltered
    // read would be denied by the rules), so a member assigned their first
    // foreign mark by someone else mid-session picks it up on the next
    // loadData; marks created in THIS session attach immediately via
    // ensureForeignSubscriptions().
    probeAndAttachForeignStreams(firmId, userId) {
      hasForeignTrademarks(firmId, userId)
        .then((has) => {
          if (this.firmId !== firmId || this.userId !== userId) return;
          if (has) {
            this.attachForeignStreams(firmId, userId);
          } else {
            // nothing there — drop anything stale from a previous firm.
            // Vacuously loaded: no foreign stream will attach this session,
            // so the fully_loaded getters must not wait on these flags.
            this.foreign_trademarks = [];
            this.foreign_dockets = [];
            this.foreign_tm_loaded = true;
            this.foreign_docket_loaded = true;
          }
        })
        .catch((error) => {
          // fail open: a probe error (offline, rules hiccup) must not hide
          // marks the old always-subscribe behavior would have shown
          console.log(error);
          if (this.firmId === firmId && this.userId !== userId) return;
          this.attachForeignStreams(firmId, userId);
        });
    },
    attachForeignStreams(firmId, userId) {
      // guard synchronously: the stream promises resolve later, so the
      // unsubscribe handles alone can't prevent a double attach
      if (this._foreignStreaming) return;
      this._foreignStreaming = true;
      streamForeignTrademarksOfUser(
        firmId,
        userId,
        this.foreignTradeMarkSnapper
      ).then((unsub) => {
        if (this.firmId !== firmId || this.userId !== userId) {
          unsub();
        } else {
          this.unsubscribeForeignTm = unsub;
        }
      });
      streamForeignDocketingOfUser(
        firmId,
        userId,
        this.foreignDocketSnapper
      ).then((unsub) => {
        if (this.firmId !== firmId || this.userId !== userId) {
          unsub();
        } else {
          this.unsubscribeForeignDocketing = unsub;
        }
      });
    },
    // called by the create/import flows right after the first foreign mark
    // is written, so this session streams it live without waiting for the
    // next loadData probe
    ensureForeignSubscriptions() {
      if (this._foreignStreaming || !this.firmId || !this.userId) return;
      this.attachForeignStreams(this.firmId, this.userId);
    },
    docketSnapper(snapshot) {
      const all_dockets = ref([]);
      all_dockets.value = snapshot.docs.map((doc) => {
        const data = doc.data();
        if (data.status === "Overdue") {
          data.status = "To Do";
        }
        return { id: doc.id, ...data };
      });
      this.us_dockets = all_dockets;
      this.docket_loaded = true;
      this.updateFilterOptions();
    },
    foreignDocketSnapper(snapshot) {
      const all_dockets = ref([]);
      all_dockets.value = snapshot.docs.map((doc) => {
        const data = doc.data();
        if (data.status === "Overdue") {
          data.status = "To Do";
        }
        // tagged here, not at merge time, so the row objects stay the
        // reactive ones (spread copies would break checkbox selection)
        return { id: doc.id, foreign: true, ...data };
      });
      this.foreign_dockets = all_dockets;
      this.foreign_docket_loaded = true;
      this.updateFilterOptions();
    },
    tradeMarkSnapper(snapshot) {
      const tms = ref([]);
      tms.value = snapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
      this.tm_loaded = true;
      this.us_trademarks = tms;
      this.updateTmFilterOptions();
    },
    foreignTradeMarkSnapper(snapshot) {
      const tms = ref([]);
      tms.value = snapshot.docs.map((doc) => {
        // tagged here, not at merge time, so the row objects stay the
        // reactive ones (spread copies would break checkbox selection)
        return { id: doc.id, foreign: true, ...doc.data() };
      });
      this.foreign_trademarks = tms;
      this.foreign_tm_loaded = true;
      this.updateTmFilterOptions();
    },
    updateCurrentTrademark(serialNumber) {
      this.currentTrademarkId = serialNumber;
    },
    setShowForeign(newVal) {
      this.showForeign = !!newVal;
      this.updateTmFilterOptions();
    },
    // `foreign` routes the write to tm-foreign (keyed by doc id); foreign
    // marks have no logs collection, so only their tm-docket tasks are
    // denormalized
    async updateAssigned(serialNumber, assigned, foreign = false) {
      // assignment is denormalized onto the mark's docketing tasks and logs
      // (same pattern as patents) so firestore.rules can scope member reads
      const firmId = this.firmId;
      try {
        await db.runTransaction(async (transaction) => {
          if (foreign) {
            await updateForeignTrademark(firmId, serialNumber, { "assigned": assigned }, transaction);
            const tasks = await getForeignDocketTasksByTrademarkId(firmId, serialNumber);
            for (const task of tasks) {
              await updateForeignDocketTask(firmId, task.id, { "assigned": assigned }, transaction);
            }
            return;
          }
          await updateTrademark(firmId, serialNumber, { "assigned": assigned }, transaction);

          const docketTasks = await getDocketTasksByTrademarkSerialNumberByDeadline(firmId, serialNumber);
          for (const docketTask of docketTasks.value) {
            await updateDocketTask(firmId, docketTask.id, { "assigned": assigned }, transaction);
          }

          const logs = await getTrademarkLogsBySerialNumber(firmId, serialNumber);
          for (const log of logs.value) {
            await updateTrademarkLog(firmId, log.id, { "assigned": assigned }, transaction);
          }
        });
      } catch (error) {
        console.error("Transaction failed: ", error);
        throw error;
      }
    },
    updateClientOfTrademark(serialNumber, clientId, foreign = false) {
      return foreign
        ? updateForeignTrademark(this.firmId, serialNumber, { "client": clientId })
        : updateTrademark(this.firmId, serialNumber, { "client": clientId });
    },
    updateTmColumns(newColumns) {
      this.trademark_columns = newColumns;
    },
    updateDocketColumns(newColumns) {
      this.the_docket_columns = newColumns;
    },
    setShowDead(newVal) {
      this.showDead = !!newVal;
      if (this.showDead) {
        this.showArchived = false;
      }
      // the Status filter options and counts are built from the base
      // visibility set — rebuild when it changes
      this.updateTmFilterOptions();
    },
    setShowArchived(newVal) {
      this.showArchived = !!newVal;
      if (this.showArchived) {
        this.showDead = false;
      }
      this.updateTmFilterOptions();
    },
    async updateArchived(serialNumber, archived, foreign = false) {
      const firmId = this.firmId;
      try {
        await db.runTransaction(async (transaction) => {
          if (foreign) {
            await updateForeignTrademark(firmId, serialNumber, { "archived": archived }, transaction);
            const tasks = await getForeignDocketTasksByTrademarkId(firmId, serialNumber);
            for (const task of tasks) {
              await updateForeignDocketTask(firmId, task.id, { "archived": archived, "status": "To Do" }, transaction);
            }
            return;
          }
          await updateTrademark(firmId, serialNumber, { "archived": archived }, transaction);

          const docketTasks = await getDocketTasksByTrademarkSerialNumberByDeadline(firmId, serialNumber);
          for (const docketTask of docketTasks.value) {
            await updateDocketTask(firmId, docketTask.id, { "archived": archived, "status": "To Do" }, transaction);
          }

          const logs = await getTrademarkLogsBySerialNumber(firmId, serialNumber);
          for (const log of logs.value) {
            await updateTrademarkLog(firmId, log.id, { "archived": archived }, transaction);
          }
        });
      } catch (error) {
        console.error("Transaction failed: ", error);
        throw error;
      }
    },
    setShowCompleted(newVal) {
      this.showCompleted = !!newVal;
      this.updateFilterOptions();
    },
    updateTmSortColumn(newVal) {
      this.trademark_sort = newVal;
    },
    updateDocketSort(newVal) {
      this.docket_sort = newVal;
    },
    updateFilterOptions() {
      this.updateEventTypeFilterOptions();
      this.updateStatusFilterOptions();
      this.updateCountryFilterOptions();
    },

    updateCountryFilterOptions() {
      // checkbox options for the docketing table's Country column. Tasks
      // carry no jurisdiction — resolve through the parent matter (same join
      // as the column renderer), mapped once so the rebuild is O(tasks).
      const countryColumn = this.the_docket_columns.find((col) => col.fieldName === "country");
      if (!countryColumn || !countryColumn.filterOptions) return;

      const existingSelections = {};
      countryColumn.filterOptions.forEach((opt) => { existingSelections[opt.label] = opt.value; });

      const countryByMatterId = {};
      for (const tm of this.the_trademarks) {
        countryByMatterId[tm.id || tm.serialNumber] = tm.foreign ? (tm.jurisdiction || "(None)") : "US";
      }
      const items = this.the_dockets.filter((item) =>
        // archived tasks never render as rows — don't count them either
        !item.archived && (this.showCompleted ? item.status == "Completed" : item.status != "Completed")
      );
      const counts = new Map();
      for (const task of items) {
        const c = countryByMatterId[task.serialNumber];
        if (!c) continue;
        counts.set(c, (counts.get(c) ?? 0) + 1);
      }
      countryColumn.filterOptions = [...counts.keys()].sort().map((label) => ({
        label,
        value: Object.hasOwn(existingSelections, label) ? existingSelections[label] : true,
        count: counts.get(label),
      }));
    },

    updateTmFilterOptions() {
      // checkbox-style options (label/value/count) for the trademark table's
      // Status, Client, and Assigned columns — same pattern as
      // updateEventTypeFilterOptions for the docketing table. Counts reflect
      // the base visibility set (archived/dead toggles), not other filters.
      const base = this.the_trademarks.filter((x) => {
        // archived mode shows US and foreign together (same gate as the table)
        if (!this.showArchived && !this.showForeign && x.foreign) return false;
        if (this.showArchived) return !!x.archived;
        if (this.showDead) return !x.archived;
        return x.alive && !x.archived;
      });
      const buildOptions = (column, valuesFor) => {
        if (!column || !column.filterOptions) return;
        const existingSelections = {};
        column.filterOptions.forEach((opt) => { existingSelections[opt.label] = opt.value; });
        const counts = new Map();
        for (const tm of base) {
          for (const v of valuesFor(tm)) {
            if (!v) continue;
            counts.set(v, (counts.get(v) ?? 0) + 1);
          }
        }
        column.filterOptions = [...counts.keys()].sort().map((label) => ({
          label,
          value: Object.hasOwn(existingSelections, label) ? existingSelections[label] : true,
          count: counts.get(label),
        }));
      };
      const cols = this.trademark_columns;
      buildOptions(cols.find((c) => c.fieldName === "alive"), (tm) => [tm.alive ? "Live" : "Dead"]);
      buildOptions(cols.find((c) => c.fieldName === "country"), (tm) =>
        [tm.foreign ? (tm.jurisdiction || "(None)") : "US"]);
      buildOptions(cols.find((c) => c.fieldName === "client"), (tm) =>
        [(tm.client && this.clientNames[tm.client]) || "(None)"]);
      buildOptions(cols.find((c) => c.fieldName === "assigned"), (tm) => {
        const names = (Array.isArray(tm.assigned) ? tm.assigned : []).map((uid) => this.memberNames[uid] || uid);
        return names.length ? names : ["(None)"];
      });
    },

    updateEventTypeFilterOptions() {
      // Get unique eventTypes from docketingItems
      const eventTypeColumn = this.the_docket_columns.find(col => col.fieldName === 'eventType');
      if (!eventTypeColumn) return;

      // Preserve existing filter selections
      const existingSelections = {};
      if (eventTypeColumn.filterOptions) {
        eventTypeColumn.filterOptions.forEach(opt => {
          existingSelections[opt.label] = opt.value;
        });
      }

      if (!this.showCompleted) {
        // archived tasks never render as rows — don't count them either
        const pendingItems = this.the_dockets.filter(item => item.status != "Completed" && !item.archived);
        const uniqueEventTypes = [...new Set(
          pendingItems
            .map(item => item.eventType)
            .filter(eventType => eventType) // filter out null/undefined
        )].sort();

        eventTypeColumn.filterOptions = uniqueEventTypes.map(eventType => ({
          label: eventType,
          // Preserve existing selection if it exists, otherwise default to true
          value: Object.hasOwn(existingSelections, eventType) ? existingSelections[eventType] : true,
          count: pendingItems.filter(item => item.eventType === eventType).length
        }));
      } else {
        const completedItems = this.the_dockets.filter(item => item.status == "Completed" && !item.archived);
        const uniqueEventTypes = [...new Set(
          completedItems
            .map(item => item.eventType)
            .filter(eventType => eventType) // filter out null/undefined
        )].sort();

        eventTypeColumn.filterOptions = uniqueEventTypes.map(eventType => ({
          label: eventType,
          // Preserve existing selection if it exists, otherwise default to true
          value: Object.hasOwn(existingSelections, eventType) ? existingSelections[eventType] : true,
          count: completedItems.filter(item => item.eventType === eventType).length
        }));
      }
    },

    updateStatusFilterOptions() {
      const statusColumn = this.the_docket_columns.find(col => col.fieldName === 'status');
      if (!statusColumn) return;

      // Preserve existing filter selections
      const existingSelections = {};
      if (statusColumn.filterOptions) {
        statusColumn.filterOptions.forEach(opt => {
          existingSelections[opt.label] = opt.value;
        });
      }

      if (!this.showCompleted) {
        const pendingItems = this.the_dockets.filter(item => item.status != "Completed" && !item.archived);
        const uniqueStatusTypes = [...new Set(
          pendingItems
            .map(item => item.status)
            .filter(status => status && status !== "Overdue") // filter out null/undefined and "Overdue"
        )].sort();

        statusColumn.filterOptions = uniqueStatusTypes.map(status => ({
          label: status,
          // Preserve existing selection if it exists, otherwise default to true
          value: Object.hasOwn(existingSelections, status) ? existingSelections[status] : true,
          count: pendingItems.filter(item => { 
            if (status === "To Do" && (item.status === "Overdue" || item.status === "To Do")) {
              return true;
            }
            if (status === "Overdue") {
              return false;
            }
            return item.status === status;
          }).length
        }));
      } else {
        statusColumn.filterOptions = [
          { 
            label: "Completed", 
            value: Object.hasOwn(existingSelections, "Completed") ? existingSelections["Completed"] : true, 
            count: this.the_dockets.filter(item => item.status == "Completed" && !item.archived).length 
          }
        ];
      }
    }
  },
});